/**
 * Synchronous utility functions for checking URL location parameters
 * These can be used early in app initialization, before React hooks are available
 */

export type UrlLocationParams = {
  locationId?: number;
  lspId?: number;
};

/**
 * Synchronously get company and location parameters from the current URL
 * @returns Object with lspId and locId from URL parameters, or undefined if not present
 */
export function getUrlLocationParameters(): UrlLocationParams {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const companyParam = urlParams.get('company');
    const locationParam = urlParams.get('location');

    if (!companyParam || !locationParam) {
      return {};
    }

    const lspId = parseInt(companyParam, 10);
    const locationId = parseInt(locationParam, 10);

    // Only return valid parameters
    if (!Number.isNaN(lspId) && !Number.isNaN(locationId)) {
      return { lspId, locationId };
    }

    return {};
  } catch (error) {
    console.warn('Error parsing URL location parameters:', error);
    return {};
  }
}

/**
 * Check if specific lspId and locId match the current URL parameters
 * @param lspId - LSP ID to check
 * @param locId - Location ID to check
 * @returns True if the provided IDs match URL parameters
 */
export function matchesUrlLocationParameters(lspId?: number, locationId?: number): boolean {
  const urlParams = getUrlLocationParameters();
  return urlParams.lspId === lspId && urlParams.locationId === locationId;
}
